/**
 * Phase 18 — in-chat render card.
 *
 * Renders the four `render_*` chat-message kinds:
 *  - `render_generating` — spinner + "Rendering — about Ns left" copy
 *  - `render_complete`   — image + Approve / Reject buttons
 *  - `render_approved`   — "✓ Approved" pill, no buttons
 *  - `render_rejected`   — dimmed image, "✗ Rejected" pill; collapses
 *                          to a one-line stub after 30 seconds
 *
 * Tap on the image opens the parent's Lightbox (callback prop).
 */

import { useEffect, useState } from "react";

import { secondsUntilReady } from "@starz/shared";
import type {
  ChatMessage,
  ChatMessageKind,
  RejectionCategory,
  RenderPayload,
} from "@starz/shared/api/types.gen";

interface Props {
  message: Pick<ChatMessage, "kind" | "render_payload">;
  /** Customer's approve/reject handlers — invoked from the card. */
  onApprove?: (renderId: string) => void;
  // Phase 18.1 — reject now optionally carries feedback so Stella can
  // steer the next [[RENDER:…]] away from rejected attributes.
  onReject?: (
    renderId: string,
    feedback?: { reason?: string; category?: RejectionCategory },
  ) => void;
  /** Open the parent's Lightbox with this image URL. */
  onOpenLightbox?: (url: string, alt: string) => void;
  /** Shown as a "Continue your Project" CTA once a render is approved. */
  onContinue?: () => void;
  /** Disables both buttons while a request is in flight. */
  busy?: boolean;
}

const REJECT_CHIPS: { key: RejectionCategory; label: string }[] = [
  { key: "colors", label: "Colors are off" },
  { key: "style", label: "Wrong style" },
  { key: "materials", label: "Wrong materials" },
  { key: "unrealistic", label: "Looks unrealistic" },
  { key: "other", label: "Something else" },
];

function chipLabel(category: RejectionCategory | null | undefined): string | null {
  if (!category) return null;
  return REJECT_CHIPS.find((c) => c.key === category)?.label ?? null;
}

function formatFeedback(payload: RenderPayload): string {
  const parts: string[] = [];
  const cat = chipLabel(payload.rejection_category);
  if (cat) parts.push(cat);
  if (payload.rejection_reason) parts.push(`"${payload.rejection_reason}"`);
  return parts.join(" — ");
}

const COLLAPSE_REJECTED_MS = 30_000;

function StatusPill({ kind }: { kind: ChatMessageKind }) {
  if (kind === "render_approved") {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700">
        ✓ Approved
      </span>
    );
  }
  if (kind === "render_rejected") {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-rose-50 px-3 py-1 text-xs font-semibold text-rose-700">
        ✗ Rejected
      </span>
    );
  }
  return null;
}

function GeneratingSpinner({ payload }: { payload: RenderPayload }) {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);
  const remaining = secondsUntilReady(payload, now);
  return (
    <div className="flex items-center gap-3 rounded-2xl rounded-bl-md border border-starz-gold/20 bg-starz-cream px-5 py-4 text-starz-charcoal">
      <span
        aria-label="Rendering"
        className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-starz-gold border-t-transparent"
      />
      <span className="text-sm">
        {remaining != null
          ? `Rendering — about ${remaining}s left…`
          : "Rendering — still working…"}
      </span>
    </div>
  );
}

export function RenderCard({
  message,
  onApprove,
  onReject,
  onOpenLightbox,
  onContinue,
  busy,
}: Props) {
  const kind = (message.kind ?? "text") as ChatMessageKind;
  const payload = message.render_payload ?? null;
  const [rejectedCollapsed, setRejectedCollapsed] = useState(false);
  // Phase 18.1 — feedback form state. Tapping Reject opens the form
  // (replaces the Approve/Reject buttons) so the customer can tell
  // Stella what to vary on the next render.
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [category, setCategory] = useState<RejectionCategory | null>(null);
  const [reason, setReason] = useState("");

  useEffect(() => {
    if (kind !== "render_rejected") return;
    const t = setTimeout(() => setRejectedCollapsed(true), COLLAPSE_REJECTED_MS);
    return () => clearTimeout(t);
  }, [kind]);

  if (!payload) {
    // Defensive — a render-kind message without a payload shouldn't
    // ship from the backend. Fall back to a generic text bubble so we
    // never blank the chat.
    return (
      <div className="flex justify-start">
        <div className="max-w-prose rounded-2xl rounded-bl-md border border-starz-gold/20 bg-starz-cream px-5 py-3 text-base text-starz-charcoal">
          (Render unavailable.)
        </div>
      </div>
    );
  }

  if (kind === "render_generating") {
    return (
      <div className="flex justify-start">
        <GeneratingSpinner payload={payload} />
      </div>
    );
  }

  if (kind === "render_rejected" && rejectedCollapsed) {
    const feedback = formatFeedback(payload);
    return (
      <div className="flex justify-start">
        <div className="rounded-2xl rounded-bl-md border border-starz-gold/20 bg-starz-cream px-4 py-2 text-xs italic text-warm-gray">
          {feedback
            ? `You rejected a render — ${feedback}`
            : "You rejected a render."}
        </div>
      </div>
    );
  }

  const isApproved = kind === "render_approved";
  const isRejected = kind === "render_rejected";
  const url = payload.url ?? null;

  return (
    <div className="flex justify-start">
      <div className="flex max-w-md flex-col gap-3 rounded-2xl rounded-bl-md border border-starz-gold/20 bg-starz-cream px-4 py-3 text-starz-charcoal">
        {url ? (
          <button
            type="button"
            onClick={() =>
              onOpenLightbox?.(url, `Render for "${payload.prompt}"`)
            }
            className="overflow-hidden rounded-lg"
          >
            <img
              src={url}
              alt={`Render of "${payload.prompt}"`}
              className={`w-full ${isRejected ? "opacity-50" : ""}`}
            />
          </button>
        ) : (
          <div className="aspect-video w-full rounded-lg bg-starz-cream/60" />
        )}
        <div className="flex items-center justify-between gap-2">
          <p className="text-xs italic text-warm-gray">"{payload.prompt}"</p>
          <StatusPill kind={kind} />
        </div>
        {isRejected && formatFeedback(payload) ? (
          <p className="text-xs text-rose-700">
            <span className="font-semibold">Your feedback:</span>{" "}
            {formatFeedback(payload)}
          </p>
        ) : null}
        {isApproved && onContinue ? (
          <button
            type="button"
            onClick={onContinue}
            className="min-h-[44px] rounded-lg bg-blue-700 px-5 py-2 text-sm font-semibold text-white transition-colors hover:bg-blue-800"
          >
            Continue your Project →
          </button>
        ) : null}
        {kind === "render_complete" && !isApproved && !isRejected ? (
          feedbackOpen ? (
            <div className="flex flex-col gap-2">
              <p className="text-xs font-semibold text-starz-navy">
                What didn't you like? (optional — Stella will use this for the
                next render)
              </p>
              <div className="flex flex-wrap gap-1.5">
                {REJECT_CHIPS.map((c) => (
                  <button
                    key={c.key}
                    type="button"
                    onClick={() => setCategory(c.key === category ? null : c.key)}
                    className={`rounded-full px-3 py-1 text-xs ${
                      category === c.key
                        ? "bg-starz-navy text-starz-off-white"
                        : "border border-starz-gold/30 text-starz-charcoal"
                    }`}
                  >
                    {c.label}
                  </button>
                ))}
              </div>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value.slice(0, 500))}
                placeholder="Add a note (optional)…"
                rows={2}
                className="resize-none rounded-md border border-starz-gold/30 bg-white px-2 py-1 text-sm text-starz-charcoal focus:outline-none focus:ring-1 focus:ring-starz-gold"
              />
              <div className="flex gap-2">
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => {
                    setFeedbackOpen(false);
                    onReject?.(payload.render_id);
                  }}
                  className="flex-1 rounded-full border border-warm-gray/30 px-4 py-2 text-sm font-semibold text-warm-gray disabled:opacity-50"
                >
                  Skip
                </button>
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => {
                    const trimmed = reason.trim();
                    setFeedbackOpen(false);
                    onReject?.(payload.render_id, {
                      reason: trimmed || undefined,
                      category: category ?? undefined,
                    });
                  }}
                  className="flex-1 rounded-full bg-rose-600 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
                >
                  Submit
                </button>
              </div>
            </div>
          ) : (
            <div className="flex gap-2">
              <button
                type="button"
                disabled={busy}
                onClick={() => onApprove?.(payload.render_id)}
                className="flex-1 rounded-full bg-starz-navy px-4 py-2 text-sm font-semibold text-starz-off-white disabled:opacity-50"
              >
                Approve
              </button>
              <button
                type="button"
                disabled={busy}
                onClick={() => setFeedbackOpen(true)}
                className="flex-1 rounded-full border border-rose-300 px-4 py-2 text-sm font-semibold text-rose-600 disabled:opacity-50"
              >
                Reject
              </button>
            </div>
          )
        ) : null}
      </div>
    </div>
  );
}
